import { useCallback, useState, type SubmitEvent } from "react";
import { catchError, of, tap } from "rxjs";

import type { APILibraryVerticalResponseType } from "@/core/data_sources/models/APILibraryVertical";
import { Button, TextInput } from "@/core/ui/atoms";
import { DrawerTemplate } from "@/core/ui/templates";
import { useStore } from "@/core/utils/hooks";
import { ErrorResponse } from "@/core/utils/request";

import { LibraryVerticalsStore } from "../data_sources/store";

type VerticalStatus = Required<APILibraryVerticalResponseType>["status"];

export function EditVerticalDrawer({
  onClose,
  onSaved,
  vertical,
}: {
  readonly onClose: () => void;
  readonly onSaved: (vertical: APILibraryVerticalResponseType) => void;
  readonly vertical: APILibraryVerticalResponseType;
}) {
  const { actions } = useStore(LibraryVerticalsStore.instance);
  const [code, setCode] = useState(vertical.code ?? "");
  const [error, setError] = useState("");
  const [name, setName] = useState(vertical.name ?? "");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<VerticalStatus>(
    vertical.status ?? "active",
  );

  const handleSubmit = useCallback(
    (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!vertical.id || !code.trim() || !name.trim()) {
        setError("Code and name are required.");
        return;
      }

      setSaving(true);
      setError("");
      actions
        .updateVertical(vertical.id!, {
          code: code.trim().toUpperCase(),
          name: name.trim(),
          status,
        })
        .pipe(
          tap(() => {
            onSaved(vertical);
          }),
          catchError((requestError) => {
            setError(
              requestError instanceof ErrorResponse
                ? requestError.error.message
                : requestError instanceof Error
                  ? requestError.message
                  : "Unable to save vertical.",
            );
            return of(null);
          }),
          tap(() => setSaving(false)),
        )
        .subscribe();
    },
    [actions, vertical.id, code, name, status, onSaved],
  );

  return (
    <DrawerTemplate
      content={
        <form
          className="space-y-4"
          id="edit-vertical-form"
          onSubmit={handleSubmit}
        >
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-ink-muted">
              Code
            </span>
            <TextInput
              onChange={(event) => setCode(event.target.value)}
              value={code}
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-ink-muted">
              Name
            </span>
            <TextInput
              onChange={(event) => setName(event.target.value)}
              value={name}
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-ink-muted">
              Status
            </span>
            <select
              className="w-full rounded-control border border-border bg-panel-muted px-3 py-1.5 text-[13px] text-ink outline-none transition-colors focus:border-cta focus:bg-surface"
              onChange={(event) =>
                setStatus(event.target.value as VerticalStatus)
              }
              value={status}
            >
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </label>
          {error ? <p className="text-xs text-critical">{error}</p> : null}
        </form>
      }
      footer={
        <>
          <Button disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={saving}
            form="edit-vertical-form"
            type="submit"
            variant="primary"
          >
            {saving ? "Saving..." : "Save vertical"}
          </Button>
        </>
      }
      header={
        <div>
          <h2 className="text-base font-semibold text-ink">Edit vertical</h2>
          <p className="mt-0.5 text-xs text-ink-subtle">{vertical.name}</p>
        </div>
      }
    />
  );
}
